import React, { useReducer, useContext, useMemo } from 'react';
import { ProductCardProps } from '@teko/types/ecommerce';

export interface HardwareLine {
  product: ProductCardProps;
  quantity: number;
}

export interface Configuration {
  [categoryId: string]: HardwareLine;
}

export interface BuildPcState {
  configurations: Array<Configuration>;
  currentConfigurationIndex: number;
}

export interface ComputedBuildPcState extends BuildPcState {
  currentConfiguration: Configuration;
}

export type BuildPcAction =
  | { type: 'NEW_CONFIGURATION' }
  | { type: 'CHANGE_CONFIGURATION'; payload: number }
  | {
      type: 'ADD_HARDWARE';
      payload: {
        categoryId: string;
        product: ProductCardProps;
      };
    }
  | {
      type: 'SET_HARDWARE_QUANTITY';
      payload: {
        categoryId: string;
        quantity: number;
      };
    }
  | {
      type: 'REMOVE_HARDWARE';
      payload: {
        categoryId: string;
      };
    }
  | {
      type: 'REDUCE_HARDWARE_QUANTITY';
      payload: {
        categoryId: string;
      };
    }
  | {
      type: 'SET_CONFIGURATIONS';
      payload: Array<Configuration>;
    };
type BuildPcDispatch = (action: BuildPcAction) => void;

const initialState: BuildPcState = {
  configurations: [{}, {}, {}],
  currentConfigurationIndex: 0,
};

export function buildPcReducer(
  state: BuildPcState,
  action: BuildPcAction,
): BuildPcState {
  switch (action.type) {
    case 'NEW_CONFIGURATION':
      return {
        ...state,
        configurations: [...state.configurations, {}],
        currentConfigurationIndex: state.configurations.length,
      };
    case 'CHANGE_CONFIGURATION':
      return {
        ...state,
        currentConfigurationIndex: action.payload,
      };
    case 'SET_CONFIGURATIONS':
      return {
        ...state,
        configurations: action.payload,
      };
    case 'ADD_HARDWARE':
      return {
        ...state,
        configurations: state.configurations.map((configuration, index) => {
          if (state.currentConfigurationIndex !== index) {
            return configuration;
          }
          let quantity = 0;
          if (!configuration[action.payload.categoryId]) {
            quantity = 1;
          }
          if (configuration[action.payload.categoryId]) {
            quantity =
              configuration[action.payload.categoryId].product.sku ===
              action.payload.product.sku
                ? configuration[action.payload.categoryId].quantity + 1
                : 1;
          }
          return {
            ...configuration,
            [action.payload.categoryId]: {
              product: action.payload.product,
              quantity,
            },
          };
        }),
      };
    case 'SET_HARDWARE_QUANTITY':
      return {
        ...state,
        configurations: state.configurations.map((configuration, index) => {
          if (state.currentConfigurationIndex !== index) {
            return configuration;
          }
          let quantity = action.payload.quantity;
          if (quantity > 99) quantity = 99;
          if (quantity < 1 || !configuration[action.payload.categoryId])
            quantity = 1;
          return {
            ...configuration,
            [action.payload.categoryId]: {
              ...configuration[action.payload.categoryId],
              quantity,
            },
          };
        }),
      };
    case 'REDUCE_HARDWARE_QUANTITY':
      return {
        ...state,
        configurations: state.configurations.map((configuration, index) => {
          if (
            state.currentConfigurationIndex !== index ||
            !configuration[action.payload.categoryId]
          ) {
            return configuration;
          }

          if (configuration[action.payload.categoryId].quantity === 1) {
            const {
              [action.payload.categoryId]: omit,
              ...remaindingConfiguration
            } = configuration;
            return remaindingConfiguration;
          }
          return {
            ...configuration,
            [action.payload.categoryId]: {
              ...configuration[action.payload.categoryId],
              quantity: configuration[action.payload.categoryId].quantity - 1,
            },
          };
        }),
      };
    case 'REMOVE_HARDWARE':
      return {
        ...state,
        configurations: state.configurations.map((configuration, index) => {
          if (state.currentConfigurationIndex !== index) {
            return configuration;
          }
          const {
            [action.payload.categoryId]: omit,
            ...remaindingConfiguration
          } = configuration;
          return {
            ...remaindingConfiguration,
          };
        }),
      };
    default:
      return state;
  }
}

const BuildPcStateContext = React.createContext<ComputedBuildPcState>(
  initialState as ComputedBuildPcState,
);
const BuildPcDispatchContext = React.createContext<BuildPcDispatch | undefined>(
  undefined,
);

export interface BuildPcProviderProps {
  children: React.ReactNode;
}
export const BuildPcProvider: React.FC<BuildPcProviderProps> = ({
  children,
}: BuildPcProviderProps) => {
  const [state, dispatch] = useReducer(buildPcReducer, initialState);
  const computedState: ComputedBuildPcState = useMemo(() => {
    return {
      ...state,
      currentConfiguration:
        state.configurations[state.currentConfigurationIndex] || {},
    };
  }, [state]);
  return (
    <BuildPcStateContext.Provider value={computedState}>
      <BuildPcDispatchContext.Provider value={dispatch}>
        {children}
      </BuildPcDispatchContext.Provider>
    </BuildPcStateContext.Provider>
  );
};

export function useBuildPcState(): ComputedBuildPcState {
  const context = useContext(BuildPcStateContext);
  if (!context) {
    throw new Error(
      'useBuildPcState must be inside a BuildPcProvider with a state value',
    );
  }
  return context;
}

export function useBuildPcDispatch(): BuildPcDispatch {
  const context = useContext(BuildPcDispatchContext);
  if (!context) {
    throw new Error(
      'useBuildPcDispatch must be inside a BuildPcProvider with a state value',
    );
  }
  return context;
}
